import React from 'react';
import { Card, Button, Row, Col, Typography } from 'antd';
import { useHistory } from 'react-router-dom';
import { ArrowRightOutlined, HeartOutlined, BuildOutlined, BulbOutlined, ApiOutlined } from '@ant-design/icons';
import calmiHeartImage from '../../assets/images/Calmi Heart.png';

const { Title, Text, Paragraph } = Typography;

const CalmiHeart = () => {
  const history = useHistory();

  const sections = [
    {
      id: 'oems',
      title: 'For OEMs',
      subtitle: 'Manufacturing Partners',
      description: 'Complete hardware solutions and manufacturing specifications for integrating Calmi technology into your plush toy production lines.',
      icon: <BuildOutlined style={{ fontSize: '48px', color: '#1890ff' }} />,
      color: '#1890ff',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      path: '/heart/oems',
      highlights: ['Hardware Specifications', 'Manufacturing Guidelines', 'Quality Standards', 'Supply Chain Support']
    },
    {
      id: 'brands',
      title: 'For Brands',
      subtitle: 'Brand Partners',
      description: 'White-label AI companion solutions that can be customized and branded for your unique market position and customer needs.',
      icon: <BulbOutlined style={{ fontSize: '48px', color: '#e74c3c' }} />,
      color: '#e74c3c',
      gradient: 'linear-gradient(135deg, #fd746c 0%, #ff9068 100%)',
      path: '/heart/brands',
      highlights: ['White-label Solutions', 'Brand Customization', 'Market Positioning', 'Customer Experience']
    },
    {
      id: 'technical',
      title: 'Technical Specs',
      subtitle: 'Architecture & Implementation',
      description: 'Comprehensive technical documentation covering AI architecture, conversation design, safety systems, and implementation guidelines.',
      icon: <ApiOutlined style={{ fontSize: '48px', color: '#52c41a' }} />,
      color: '#52c41a',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      path: '/heart/technical',
      highlights: ['AI Architecture', 'Safety & Privacy', 'Implementation Guide', 'API Documentation']
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)',
        padding: '80px 0',
        marginBottom: '60px',
        position: 'relative'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', textAlign: 'center' }}>
          <div style={{ marginBottom: '24px' }}>
            <img 
              src={calmiHeartImage} 
              alt="Calmi Heart"
              style={{ 
                height: '120px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentNode.innerHTML = '<div style="font-size: 72px; color: white;">💖</div>';
              }}
            />
          </div>
          <Title level={1} style={{ color: 'white', fontSize: '3.5rem', marginBottom: '16px' }}>
            Calmi Heart
          </Title>
          <Paragraph style={{ 
            color: 'rgba(255,255,255,0.9)', 
            fontSize: '24px', 
            maxWidth: '800px', 
            margin: '0 auto 40px' 
          }}>
            The AI companion technology powering the next generation of emotional wellness products. 
            White-label solutions for brands and OEMs.
          </Paragraph>
          
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            margin: '0 auto',
            backdropFilter: 'blur(10px)'
          }}>
            <Text style={{ color: 'white', fontSize: '16px', fontWeight: 500 }}>
              ✨ Transform any plush toy into an intelligent, caring companion with our proven AI technology
            </Text>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        
        {/* Value Proposition */}
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <Title level={2} style={{ marginBottom: '24px' }}>
            Proven Technology, Endless Possibilities
          </Title>
          <Paragraph style={{ fontSize: '18px', color: '#666', maxWidth: '800px', margin: '0 auto 40px' }}>
            Calmi Heart is the white-label version of our award-winning AI companion technology. 
            We provide everything you need to create intelligent, safe, and emotionally supportive 
            plush products under your own brand.
          </Paragraph>
          
          <Row gutter={[24, 24]} justify="center">
            <Col xs={24} md={8}>
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>🛡️</div>
                <Title level={4}>Child-Safe AI</Title>
                <Text>COPPA-compliant with comprehensive safety filters</Text>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>🎯</div>
                <Title level={4}>Proven Results</Title>
                <Text>Battle-tested technology with real market success</Text>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>⚡</div>
                <Title level={4}>Quick Integration</Title>
                <Text>Full implementation support from concept to market</Text>
              </div>
            </Col>
          </Row>
        </div>

        {/* Sections Grid */}
        <Row gutter={[32, 32]} justify="center" style={{ marginBottom: '80px' }}>
          {sections.map(section => (
            <Col xs={24} lg={8} key={section.id}>
              <Card
                hoverable
                style={{
                  height: '100%',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: 'none',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                }}
                bodyStyle={{ padding: 0 }}
                onClick={() => history.push(section.path)}
              >
                {/* Header with gradient */}
                <div style={{
                  background: section.gradient,
                  padding: '40px 24px',
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <div style={{ marginBottom: '16px' }}>
                    {React.cloneElement(section.icon, { style: { fontSize: '48px', color: 'white' } })}
                  </div>
                  <Title level={2} style={{ color: 'white', margin: '0 0 8px 0' }}>
                    {section.title}
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
                    {section.subtitle}
                  </Text>
                </div>

                {/* Content */}
                <div style={{ padding: '32px 24px' }}>
                  <Paragraph style={{ 
                    fontSize: '16px', 
                    color: '#666', 
                    marginBottom: '24px',
                    lineHeight: '1.6'
                  }}>
                    {section.description}
                  </Paragraph>

                  {/* Highlights */}
                  <div style={{ marginBottom: '32px' }}>
                    <Text strong style={{ fontSize: '14px', color: '#333', marginBottom: '12px', display: 'block' }}>
                      What You'll Find:
                    </Text>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {section.highlights.map((highlight, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <HeartOutlined style={{ 
                            fontSize: '12px', 
                            color: section.color 
                          }} />
                          <Text style={{ fontSize: '14px', color: '#666' }}>{highlight}</Text>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Button */}
                  <Button
                    type="primary"
                    size="large"
                    icon={<ArrowRightOutlined />}
                    block
                    style={{
                      background: section.color,
                      borderColor: section.color,
                      borderRadius: '8px',
                      height: '48px',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      history.push(section.path);
                    }}
                  >
                    Learn More
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* CTA Section */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 40px',
          textAlign: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          marginBottom: '60px'
        }}>
          <Title level={2} style={{ marginBottom: '24px' }}>
            Ready to Transform Your Products?
          </Title>
          <Paragraph style={{ 
            fontSize: '18px', 
            color: '#666', 
            maxWidth: '600px', 
            margin: '0 auto 40px' 
          }}>
            Whether you're a manufacturer looking to add AI capabilities or a brand wanting to create 
            meaningful emotional connections with your customers, Calmi Heart provides the foundation 
            you need.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            style={{
              background: '#ff6b6b',
              borderColor: '#ff6b6b',
              height: '56px',
              fontSize: '18px',
              fontWeight: 'bold',
              padding: '0 40px',
              borderRadius: '28px'
            }}
          >
            Contact Us Today
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CalmiHeart;